import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { useSchemaKey } from '@/composables/schema'
import { useApiStore } from '@/stores/api'
import { useAppContextStore } from '@/stores/appContext'
import { useFormEditPremiumStore } from '@/stores/form/editPremium'

import Payload from '@/lib/Payload'
import { hotReloadStore } from '@/utils/build'

import type { LegacyQuoteReviewSchema } from '@policyfly/schema/types/schemas/legacyQuotereview'
import type { QuoteFormSource, QuoteSection, QuoteSchema } from '@policyfly/schema/types/schemas/quote'
import type { QuoteReviewSchema } from '@policyfly/schema/types/schemas/quotereview'
import type { FormSchema } from '@policyfly/schema/types/shared/formSchema'
import type { Ref, ComputedRef } from 'vue'

export interface FormQuoteStore {
  file: Ref<QuoteSchema | null>
  reviewData: Ref<LegacyQuoteReviewSchema | QuoteReviewSchema>
  overlay: Ref<boolean>
  activeQuotePayload: ComputedRef<Payload | null>
  multiQuoteLoader: ComputedRef<QuoteSchema['multiQuoteLoader'] | null>
  schema: ComputedRef<FormSchema<QuoteFormSource, QuoteSection> | null>
  loadSections: () => Promise<QuoteSchema['sections'] | undefined>
  loadReviewData: () => Promise<LegacyQuoteReviewSchema | QuoteReviewSchema | undefined>
}

export const useFormQuoteStore = defineStore('formQuote', (): FormQuoteStore => {
  const file: FormQuoteStore['file'] = ref<QuoteSchema | null>(null)
  const reviewData: FormQuoteStore['reviewData'] = ref({} as LegacyQuoteReviewSchema)
  const overlay: FormQuoteStore['overlay'] = ref(false)

  const schema: FormQuoteStore['schema'] = computed(() => file.value
    ? {
        sections: file.value.sections,
        contextFields: file.value.contextFields,
        rating: file.value.rating,
      }
    : null,
  )
  const activeQuotePayload: FormQuoteStore['activeQuotePayload'] = computed(() => {
    const appCtx = useAppContextStore()
    const editPrem = useFormEditPremiumStore()
    const quote = editPrem.derivedDataQuotes[0] ||
      appCtx.loadedApplicationData.selectedQuote ||
      null
    if (!quote || !quote.responses) return null
    return Array.isArray(quote.responses)
      ? Payload.fromResponses(quote.responses)
      : Payload.fromResponseBlob(quote.responses)
  })
  const multiQuoteLoader: FormQuoteStore['multiQuoteLoader'] = computed(() => file.value?.multiQuoteLoader ?? null)

  const apiStore = useApiStore()

  const { getKey: getQuoteKey, updateKey: updateQuoteKey } = useSchemaKey({ data: file })
  const loadSections: FormQuoteStore['loadSections'] = async () => {
    const { key, shouldLoad } = getQuoteKey()
    if (!shouldLoad) return file.value?.sections
    const result = await apiStore.schema.quote(key)
    if (result.type.oneofKind !== 'legacy') {
      throw new Error('Unsupported schema type')
    }
    const mod = JSON.parse(result.type.legacy.json) as QuoteSchema
    file.value = mod
    updateQuoteKey(key)
    return mod.sections
  }

  const { getKey: getReviewKey, updateKey: updateReviewKey } = useSchemaKey({ data: reviewData })
  const loadReviewData: FormQuoteStore['loadReviewData'] = async () => {
    const { key, shouldLoad } = getReviewKey()
    if (!shouldLoad) return reviewData.value
    reviewData.value = {} as QuoteReviewSchema
    const result = await apiStore.schema.quoteReview(key)
    if (result.type.oneofKind !== 'legacy') {
      throw new Error('Unsupported schema type')
    }
    const mod: LegacyQuoteReviewSchema | QuoteReviewSchema = JSON.parse(result.type.legacy.json)
    reviewData.value = mod
    updateReviewKey(key)
    return mod
  }

  return {
    file,
    reviewData,
    overlay,
    activeQuotePayload,
    multiQuoteLoader,
    schema,
    loadSections,
    loadReviewData,
  }
})

hotReloadStore(useFormQuoteStore)
